import React from 'react'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Icon,
  IconButton,
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from '@shopflo/ui'
import { CART_ELEMENTS } from './config'
import { ShopfloImageDomain } from 'src/lib/utils/constants'
import { useCartContext } from 'src/lib/contexts/CartContext'
import { Eye, EyeSlash } from '@phosphor-icons/react'
import { classNames } from 'src/lib/utils/helpers'
import { CartElementType, useCartLayoutContext } from 'src/lib/contexts/CartLayoutContext'
import { useQuickAddOnPreviewTable } from 'src/lib/hooks/useQuickAddOnPreviewTable'
import { QuickAddOnAddonLevel } from 'src/lib/types/quickAddOnV2'
import { useCartConfigItemHighlight } from './useCartConfigItemHighlight'

export const CartElementItem = ({
  element,
  selectedElement,
  selectedFromPreview,
  setSelectedElement,
  visible,
  onToggleVisibility,
  accordianClassNames = ''
}: {
  element: typeof CART_ELEMENTS[number]
  selectedElement: CartElementType | null
  selectedFromPreview: boolean
  setSelectedElement: (payload: CartElementType | null, options?: { fromPreview?: boolean }) => void
  visible?: boolean
  onToggleVisibility?: (value: boolean) => void
  accordianClassNames?: string
}) => {
  const Component = element.component
  const isSelected = selectedElement === element.type
  const { itemRef, highlightRef, isOpen, handleValueChange, highlightOverlayClassName } = useCartConfigItemHighlight(
    element.type as CartElementType,
    isSelected,
    selectedFromPreview,
    selectedElement,
    setSelectedElement
  )

  return (
    <div ref={itemRef} className='relative'>
      {true && <div ref={highlightRef} className={highlightOverlayClassName} aria-hidden />}
      <Accordion
        key={element.type}
        type='single'
        collapsible
        value={isOpen ? element.type : ''}
        onValueChange={handleValueChange}
        className={classNames(
          'relative w-full px-3 py-2 hover:rounded-none',
          isOpen
            ? 'bg-zinc-500/5 border-b-1 border-zinc-500/8 mb-0.5'
            : 'border-b-1 border-zinc-500/6 hover:bg-zinc-500/6'
        )}
      >
        <AccordionItem value={element.type}>
          <AccordionTrigger className='py-0 flex items-center'>
            <div className='flex items-center justify-between w-full'>
              <div className='flex items-center w-full gap-2 min-h-7'>
                {typeof element.icon === 'string' ? (
                  <img
                    src={`${ShopfloImageDomain}/${element.icon}.svg`}
                    alt={element.title}
                    className='w-5 h-5 object-contain'
                  />
                ) : (
                  <Icon
                    IconComponent={element.icon}
                    size='sm'
                    iconProps={{
                      weight: 'fill'
                    }}
                    className='text-zinc-500'
                  />
                )}
                <p
                  className={classNames(
                    'text-text-sm-medium text-zinc-800',
                    visible === false ? 'text-zinc-800/60' : 'text-zinc-800/100'
                  )}
                >
                  {element.title}
                </p>
              </div>
              {onToggleVisibility && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconButton
                      className='mr-1'
                      variant='ghost'
                      size='xs'
                      IconComponent={visible ? Eye : EyeSlash}
                      iconProps={{ weight: 'regular' }}
                      onClick={e => {
                        e.stopPropagation()
                        onToggleVisibility(!visible!)
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent side='top'>Show/Hide Element</TooltipContent>
                </Tooltip>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className='pt-4 px-2'>
              <Component />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}

export const CartElements = () => {
  const {
    state: { progressBar },
    getters: { cartBannerUIConfigData, cartBottomBannerUIConfigData },
    actions: { setCartBannerConfig, setIsProgressBarEnabled, setCartBottomBannerConfig }
  } = useCartContext()

  const {
    getters: { selectedElement, selectedFromPreview },
    actions: { setSelectedElement }
  } = useCartLayoutContext()

  const quickAddOnTable = useQuickAddOnPreviewTable()

  const cartLevelAddons = quickAddOnTable.filter(
    addon =>
      addon.addonLevel === QuickAddOnAddonLevel.CART &&
      ['DEFAULT', 'CART'].includes(addon.uiAsset) &&
      addon.status === true
  )

  /**
   * Determines if an element should be shown in the UI
   * This function centralizes all visibility logic for scalability
   */
  const shouldShowElement = (type: typeof CART_ELEMENTS[number]['type']): boolean => {
    switch (type) {
      case 'ADD_ON':
        return cartLevelAddons.length > 0
      default:
        return true
    }
  }

  const featureEnabledMap: Partial<
    Record<typeof CART_ELEMENTS[number]['type'], { value: boolean; setValue: (value: boolean) => void }>
  > = {
    TOP_BANNER: {
      value: cartBannerUIConfigData.enabled,
      setValue: (value: boolean) => setCartBannerConfig({ ...cartBannerUIConfigData, enabled: value })
    },
    MILESTONE: {
      value: progressBar.isEnabled,
      setValue: (value: boolean) => setIsProgressBarEnabled(value)
    },
    BOTTOM_BANNER: {
      value: cartBottomBannerUIConfigData.enabled,
      setValue: (value: boolean) => setCartBottomBannerConfig({ ...cartBottomBannerUIConfigData, enabled: value })
    }
  }

  return (
    <div className='space-y-0 bg-zinc-50 rounded-lg overflow-hidden'>
      {CART_ELEMENTS.filter(element => shouldShowElement(element.type)).map(element => (
        <CartElementItem
          key={element.type}
          element={element}
          selectedElement={selectedElement}
          selectedFromPreview={selectedFromPreview}
          setSelectedElement={setSelectedElement}
          visible={featureEnabledMap[element.type]?.value}
          onToggleVisibility={featureEnabledMap[element.type]?.setValue}
        />
      ))}
    </div>
  )
}
